import { DEBUG } from '../config';

const CACHE_ERROR_KEY = 'cache_error_count';
const MAX_CACHE_ERRORS = 3;
const ERROR_RESET_TIME = 60000; // 1分钟

/**
 * 检测并恢复缓存错误
 * 当检测到 importScripts 或其他缓存相关错误时，自动清理缓存
 */
export async function detectAndRecoverCacheError(error: Error): Promise<boolean> {
  const errorMessage = error.message || error.toString();
  
  // 检测是否是缓存相关错误
  const isCacheError = 
    errorMessage.includes('importScripts') ||
    errorMessage.includes('Failed to execute') ||
    errorMessage.includes('WorkerGlobalScope') ||
    errorMessage.includes('BigInteger') ||
    errorMessage.includes('node_modules') ||
    errorMessage.includes('.js failed to load');
  
  if (!isCacheError) {
    return false;
  }
  
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.warn('[Cache Recovery] Detected cache error:', errorMessage);
  }
  
  // 检查错误次数，防止无限循环
  const errorCount = getErrorCount();
  if (errorCount >= MAX_CACHE_ERRORS) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error('[Cache Recovery] Too many cache errors, manual intervention required');
    }
    return false;
  }
  
  incrementErrorCount();
  
  try {
    await clearAllCache();
    
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[Cache Recovery] Cache cleared successfully, reloading...');
    }
    
    // 延迟刷新，确保清理完成
    setTimeout(() => {
      window.location.reload();
    }, 500);
    
    return true;
  } catch (err) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error('[Cache Recovery] Failed to clear cache:', err);
    }
    return false;
  }
}

/**
 * 清除所有缓存
 */
export async function clearAllCache(): Promise<void> {
  const tasks: Promise<any>[] = [];
  
  // 1. 注销所有 Service Workers
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    tasks.push(...registrations.map((r) => r.unregister()));
  }
  
  // 2. 清除所有 Cache Storage
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    tasks.push(...cacheNames.map((name) => caches.delete(name)));
  }
  
  // 3. 清除 IndexedDB（保留用户数据，只清除缓存相关的）
  if ('indexedDB' in window) {
    try {
      const databases = await indexedDB.databases();
      for (const db of databases) {
        // 只删除缓存相关的数据库，保留用户数据
        if (db.name && (
          db.name.includes('cache') || 
          db.name.includes('asset') ||
          db.name.includes('worker')
        )) {
          indexedDB.deleteDatabase(db.name);
        }
      }
    } catch (err) {
      // 某些浏览器不支持 indexedDB.databases()
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn('[Cache Recovery] Could not enumerate IndexedDB databases:', err);
      }
    }
  }
  
  await Promise.all(tasks);
  
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Cache Recovery] All caches cleared');
  }
}

/**
 * 获取错误计数
 */
function getErrorCount(): number {
  try {
    const data = sessionStorage.getItem(CACHE_ERROR_KEY);
    if (!data) return 0;
    
    const { count, timestamp } = JSON.parse(data);
    
    // 如果超过重置时间，重置计数
    if (Date.now() - timestamp > ERROR_RESET_TIME) {
      sessionStorage.removeItem(CACHE_ERROR_KEY);
      return 0;
    }
    
    return count || 0;
  } catch {
    return 0;
  }
}

/**
 * 增加错误计数
 */
function incrementErrorCount(): void {
  try {
    const count = getErrorCount() + 1;
    sessionStorage.setItem(CACHE_ERROR_KEY, JSON.stringify({
      count,
      timestamp: Date.now(),
    }));
  } catch {
    // Ignore
  }
}

/**
 * 重置错误计数
 */
export function resetErrorCount(): void {
  try {
    sessionStorage.removeItem(CACHE_ERROR_KEY);
  } catch {
    // Ignore
  }
}

/**
 * 检查是否需要显示清理缓存提示
 */
export function shouldShowCacheClearHint(): boolean {
  return getErrorCount() >= MAX_CACHE_ERRORS;
}
